import React, { useEffect, useMemo, useState } from 'react';
import {
  BadgeDollarSign,
  BriefcaseBusiness,
  BrainCircuit,
  ChevronLeft,
  ChevronRight,
  CircleDollarSign,
  Cloud,
  Headset,
  House,
  KeyRound,
  LifeBuoy,
  MonitorSmartphone,
  Network,
  Printer,
  Router,
  ShieldCheck,
  ShieldEllipsis,
  UserCog,
  Wifi,
} from 'lucide-react';

const siteUrl = 'https://www.fixourtech.com';
const socialImageUrl = `${siteUrl}/logo.png`;
const phoneHref = 'tel:+19182367980';
const contactPath = '/contact.html';
const pricingPath = '/pricing.html';

const pageContent = {
  home: {
    navKey: 'home',
    path: '/',
    h1: 'Tulsa, OK residential and business IT support',
    seoTitle: 'FixOURTech | Tulsa, OK Residential and Business IT Support',
    seoDescription:
      'FixOURTech provides residential support, small business IT support, and AI training in Tulsa, OK and surrounding areas. Hourly rates start at $99/HR.',
    rateText: 'Hourly rates starting at $99/HR',
    slides: [
      {
        title: 'Residential Support',
        text: 'Fast help for home computers, Wi-Fi issues, printers, and device setup across Tulsa, Broken Arrow, Jenks, Bixby, Owasso, and nearby areas.',
        image:
          'https://images.unsplash.com/photo-1522202176988-66273c2fd55f?auto=format&fit=crop&w=1600&q=80',
      },
      {
        title: 'Business Support',
        text: 'Reliable IT support for users, devices, Microsoft 365, and day-to-day operations for Tulsa-area small businesses.',
        image:
          'https://images.unsplash.com/photo-1517048676732-d65bc937f952?auto=format&fit=crop&w=1600&q=80',
      },
      {
        title: 'AI Training',
        text: 'Practical AI guidance to help teams across the Tulsa metro use modern tools without confusion.',
        image:
          'https://images.unsplash.com/photo-1674027444485-cec3da58eef4?auto=format&fit=crop&w=1600&q=80',
      },
    ],
    sectionLabel: 'Tulsa Area Technology Coverage',
    sectionTitle: ['The systems and tools', 'we support every day.'],
    sectionText:
      'FixOURTech supports the core technology homes and small businesses depend on across Tulsa, OK and surrounding areas, without unnecessary complexity.',
    coverage: [
      {
        icon: Wifi,
        title: 'Wi-Fi and Networking',
        text: 'Wi-Fi coverage improvements, router troubleshooting, and small office network optimization.',
      },
      {
        icon: Cloud,
        title: 'Microsoft 365 and Cloud',
        text: 'Microsoft 365 administration including email, licensing, SharePoint, and tenant management.',
      },
      {
        icon: ShieldCheck,
        title: 'Security Basics',
        text: 'Patch management, multi-factor authentication, endpoint protection, and practical hardening.',
      },
      {
        icon: UserCog,
        title: 'User Support',
        text: 'User onboarding, account access issues, workstation setup, and daily support requests.',
      },
      {
        icon: Network,
        title: 'Infrastructure',
        text: 'Printers, switches, workstations, cabling, and core systems that keep the office running.',
      },
      {
        icon: BrainCircuit,
        title: 'AI Enablement',
        text: 'Practical AI training to help teams adopt modern tools with confidence and clarity.',
      },
      {
        icon: Headset,
        title: 'Help Desk Coverage',
        text: 'Responsive troubleshooting for recurring technical issues, outages, and urgent blockers.',
      },
      {
        icon: BriefcaseBusiness,
        title: 'Small Business IT',
        text: 'Right-sized MSP support built for growing teams that need stability without enterprise overhead.',
      },
    ],
  },
  residential: {
    navKey: 'residential',
    path: '/residential',
    h1: 'Residential IT support in Tulsa, OK',
    seoTitle: 'Residential IT Support | Tulsa, OK | FixOURTech',
    seoDescription:
      'Residential IT support in Tulsa, OK and surrounding areas. FixOURTech helps with home Wi-Fi, computers, printers, smart devices, and remote support starting at $99/HR.',
    rateText: 'Residential service starting at $99/HR',
    slides: [
      {
        title: 'Home Computer Support',
        text: 'Laptop and desktop troubleshooting, tune-ups, setup help, and practical repair guidance for Tulsa-area households.',
        image:
          'https://images.unsplash.com/photo-1516321497487-e288fb19713f?auto=format&fit=crop&w=1600&q=80',
      },
      {
        title: 'Wi-Fi and Device Setup',
        text: 'Stronger home Wi-Fi, printer setup, streaming device help, and better coverage where you actually use it.',
        image:
          'https://images.unsplash.com/photo-1516321318423-f06f85e504b3?auto=format&fit=crop&w=1600&q=80',
      },
      {
        title: 'Smart Home and Everyday Tech',
        text: 'Support for smart home devices, account issues, passwords, and the frustrating day-to-day tech problems that waste your time.',
        image:
          'https://images.unsplash.com/photo-1558002038-1055907df827?auto=format&fit=crop&w=1600&q=80',
      },
    ],
    sectionLabel: 'Residential Support in Tulsa, OK',
    sectionTitle: ['Home technology support', 'for Tulsa and surrounding areas.'],
    sectionText:
      'FixOURTech helps homeowners in Tulsa, Broken Arrow, Bixby, Jenks, Owasso, and nearby communities keep devices, Wi-Fi, and home tech working reliably.',
    coverage: [
      {
        icon: MonitorSmartphone,
        title: 'Computers and Devices',
        text: 'Setup, troubleshooting, cleanup, and performance help for laptops, desktops, tablets, and phones.',
      },
      {
        icon: Wifi,
        title: 'Home Wi-Fi',
        text: 'Coverage improvements, router setup, dead-zone reduction, and better in-home connectivity.',
      },
      {
        icon: Printer,
        title: 'Printers and Scanners',
        text: 'Printer setup, wireless connection issues, and scanner troubleshooting without the usual frustration.',
      },
      {
        icon: MonitorSmartphone,
        title: 'Smart Home Devices',
        text: 'Help with cameras, speakers, thermostats, streaming boxes, and smart device integrations.',
      },
      {
        icon: ShieldCheck,
        title: 'Virus and Security Cleanup',
        text: 'Malware checks, account security improvements, and practical steps to keep home tech safer.',
      },
      {
        icon: KeyRound,
        title: 'Passwords and Accounts',
        text: 'Password resets, account access help, email setup, and recovering from lockout situations.',
      },
      {
        icon: Cloud,
        title: 'Backups and Cloud Sync',
        text: 'File backup setup, cloud storage troubleshooting, and making sure important data is protected.',
      },
      {
        icon: Headset,
        title: 'Remote Help',
        text: 'Fast remote support for many common home technology issues across the Tulsa metro.',
      },
    ],
  },
  business: {
    navKey: 'business',
    path: '/business',
    h1: 'Business IT support in Tulsa, OK',
    seoTitle: 'Business IT Support | Tulsa, OK | FixOURTech',
    seoDescription:
      'Business IT support in Tulsa, OK for small teams that need Microsoft 365 help, office network support, user onboarding, and practical MSP-style service.',
    rateText: 'Business support starting at $99/HR',
    slides: [
      {
        title: 'Managed IT Support',
        text: 'Day-to-day IT support for Tulsa-area businesses that need responsive help without enterprise overhead.',
        image:
          'https://images.unsplash.com/photo-1517048676732-d65bc937f952?auto=format&fit=crop&w=1600&q=80',
      },
      {
        title: 'Office Network Stability',
        text: 'Support for users, devices, Wi-Fi, printers, and office connectivity that keeps teams productive.',
        image:
          'https://images.unsplash.com/photo-1516321318423-f06f85e504b3?auto=format&fit=crop&w=1600&q=80',
      },
      {
        title: 'Microsoft 365 and Security',
        text: 'Tenant administration, user onboarding, security basics, and practical IT management for growing teams.',
        image:
          'https://images.unsplash.com/photo-1454165804606-c3d57bc86b40?auto=format&fit=crop&w=1600&q=80',
      },
    ],
    sectionLabel: 'Business IT in Tulsa, OK',
    sectionTitle: ['Business technology support', 'built for local teams.'],
    sectionText:
      'FixOURTech supports Tulsa-area small businesses with practical MSP-style service, user support, Microsoft 365 administration, and network stability.',
    coverage: [
      {
        icon: Cloud,
        title: 'Microsoft 365',
        text: 'Email, licensing, SharePoint, Teams, and day-to-day Microsoft 365 administration.',
      },
      {
        icon: UserCog,
        title: 'User Onboarding',
        text: 'New user setup, permissions, account access, and role-based configuration for small teams.',
      },
      {
        icon: Router,
        title: 'Network and Firewall',
        text: 'Office Wi-Fi, router issues, firewall tuning, and connectivity troubleshooting.',
      },
      {
        icon: ShieldCheck,
        title: 'Security Controls',
        text: 'MFA, patching, endpoint protection, and practical security hardening for daily operations.',
      },
      {
        icon: MonitorSmartphone,
        title: 'Endpoint Management',
        text: 'Workstations, laptops, and business devices kept current, stable, and easier to support.',
      },
      {
        icon: Network,
        title: 'Printers and Infrastructure',
        text: 'Printers, switches, cabling, and shared office systems that need to stay dependable.',
      },
      {
        icon: Headset,
        title: 'Help Desk Support',
        text: 'Responsive support for the everyday user issues that interrupt real work.',
      },
      {
        icon: BrainCircuit,
        title: 'AI Training for Teams',
        text: 'Practical training to help your business use AI tools productively and responsibly.',
      },
    ],
  },
};

function getCurrentPageKey(pathname) {
  if (pathname === '/residential' || pathname === '/residential/') {
    return 'residential';
  }

  if (pathname === '/business' || pathname === '/business/') {
    return 'business';
  }

  return 'home';
}

function upsertMeta(name, content, attr = 'name') {
  let element = document.head.querySelector(`meta[${attr}="${name}"]`);
  if (!element) {
    element = document.createElement('meta');
    element.setAttribute(attr, name);
    document.head.appendChild(element);
  }
  element.setAttribute('content', content);
}

function upsertLink(rel, href) {
  let element = document.head.querySelector(`link[rel="${rel}"]`);
  if (!element) {
    element = document.createElement('link');
    element.setAttribute('rel', rel);
    document.head.appendChild(element);
  }
  element.setAttribute('href', href);
}

function upsertJsonLd(id, data) {
  let element = document.head.querySelector(`#${id}`);
  if (!element) {
    element = document.createElement('script');
    element.type = 'application/ld+json';
    element.id = id;
    document.head.appendChild(element);
  }
  element.textContent = JSON.stringify(data);
}

function App() {
  const pageKey = getCurrentPageKey(window.location.pathname);
  const page = useMemo(() => pageContent[pageKey], [pageKey]);
  const [activeSlide, setActiveSlide] = useState(0);
  const canonicalUrl = `${siteUrl}${page.path === '/' ? '' : page.path}`;

  useEffect(() => {
    document.title = page.seoTitle;
    upsertMeta('description', page.seoDescription);
    upsertMeta('robots', 'index, follow, max-image-preview:large');
    upsertMeta('og:type', 'website', 'property');
    upsertMeta('og:site_name', 'FixOURTech', 'property');
    upsertMeta('og:title', page.seoTitle, 'property');
    upsertMeta('og:description', page.seoDescription, 'property');
    upsertMeta('og:url', canonicalUrl, 'property');
    upsertMeta('og:locale', 'en_US', 'property');
    upsertMeta('og:image', socialImageUrl, 'property');
    upsertMeta('og:image:alt', 'FixOURTech logo', 'property');
    upsertMeta('twitter:card', 'summary_large_image');
    upsertMeta('twitter:title', page.seoTitle);
    upsertMeta('twitter:description', page.seoDescription);
    upsertMeta('twitter:image', socialImageUrl);
    upsertLink('canonical', canonicalUrl);

    upsertJsonLd('fixourtech-localbusiness', {
      '@context': 'https://schema.org',
      '@type': 'LocalBusiness',
      '@id': `${siteUrl}/#localbusiness`,
      name: 'FixOURTech',
      image: socialImageUrl,
      areaServed: ['Tulsa, OK', 'Broken Arrow, OK', 'Jenks, OK', 'Bixby, OK', 'Owasso, OK'],
      telephone: '+1-918-236-7980',
      url: siteUrl,
      serviceArea: {
        '@type': 'Place',
        name: 'Tulsa, OK and surrounding areas',
      },
      address: {
        '@type': 'PostalAddress',
        addressLocality: 'Tulsa',
        addressRegion: 'OK',
        addressCountry: 'US',
      },
    });

    upsertJsonLd('fixourtech-website', {
      '@context': 'https://schema.org',
      '@type': 'WebSite',
      '@id': `${siteUrl}/#website`,
      name: 'FixOURTech',
      url: siteUrl,
    });

    upsertJsonLd('fixourtech-webpage', {
      '@context': 'https://schema.org',
      '@type': 'WebPage',
      '@id': `${canonicalUrl}#webpage`,
      name: page.seoTitle,
      description: page.seoDescription,
      url: canonicalUrl,
      isPartOf: {
        '@id': `${siteUrl}/#website`,
      },
      about: {
        '@id': `${siteUrl}/#localbusiness`,
      },
    });
  }, [canonicalUrl, page]);

  useEffect(() => {
    setActiveSlide(0);
  }, [pageKey]);

  useEffect(() => {
    const timer = window.setInterval(() => {
      setActiveSlide((current) => (current + 1) % page.slides.length);
    }, 5500);

    return () => window.clearInterval(timer);
  }, [page.slides.length]);

  const goToPreviousSlide = () => {
    setActiveSlide((current) => (current - 1 + page.slides.length) % page.slides.length);
  };

  const goToNextSlide = () => {
    setActiveSlide((current) => (current + 1) % page.slides.length);
  };

  return (
    <div className="site-shell">
      <header className="topbar">
        <a className="brand" href="/" aria-label="FixOURTech home">
          <ShieldEllipsis className="brand-icon" aria-hidden="true" />
          <span className="brand-wordmark">
            Fix<span className="brand-wordmark-accent">OUR</span>Tech
          </span>
        </a>

        <nav className="nav" aria-label="Primary navigation">
          <a className={page.navKey === 'residential' ? 'is-active' : ''} href="/residential">
            <House size={18} aria-hidden="true" />
            <span>Residential</span>
          </a>
          <a className={page.navKey === 'business' ? 'is-active' : ''} href="/business">
            <BriefcaseBusiness size={18} aria-hidden="true" />
            <span>Business</span>
          </a>
          <a href={pricingPath}>
            <CircleDollarSign size={18} aria-hidden="true" />
            <span>Pricing</span>
          </a>
          <a href={contactPath}>
            <LifeBuoy size={18} aria-hidden="true" />
            <span>Contact Us</span>
          </a>
        </nav>

        <a className="button button-primary topbar-cta" href={contactPath}>
          Contact Us
        </a>
      </header>

      <main id="home">
        <section className="hero-slideshow" aria-label="Service highlights">
          <div className="hero-slides" style={{ transform: `translateX(-${activeSlide * 100}%)` }}>
            {page.slides.map((panel) => (
              <article
                className="hero-slide"
                key={panel.title}
                style={{
                  backgroundImage: `linear-gradient(180deg, rgba(7, 16, 31, 0.16) 0%, rgba(7, 16, 31, 0.72) 100%), url(${panel.image})`,
                }}
              >
                <div className="hero-panel-content">
                  <div className="rate-callout rate-callout-prominent">
                    <BadgeDollarSign size={18} aria-hidden="true" />
                    <span>{page.rateText}</span>
                  </div>
                  <p className="eyebrow">Tulsa, OK and Surrounding Areas</p>
                  <h1>{page.h1}</h1>
                  <h2 className="hero-slide-title">{panel.title}</h2>
                  <p className="hero-panel-text">{panel.text}</p>
                  <a className="button button-primary" href={contactPath}>
                    Get Started
                  </a>
                </div>
              </article>
            ))}
          </div>

          <div className="hero-controls">
            <button className="hero-control" type="button" onClick={goToPreviousSlide} aria-label="Previous slide">
              <ChevronLeft size={20} />
            </button>
            <div className="hero-dots" aria-label="Slide navigation">
              {page.slides.map((panel, index) => (
                <button
                  key={panel.title}
                  className={`hero-dot${index === activeSlide ? ' is-active' : ''}`}
                  type="button"
                  onClick={() => setActiveSlide(index)}
                  aria-label={`Show ${panel.title}`}
                />
              ))}
            </div>
            <button className="hero-control" type="button" onClick={goToNextSlide} aria-label="Next slide">
              <ChevronRight size={20} />
            </button>
          </div>
        </section>

        <section className="coverage-section" id="coverage">
          <div className="coverage-container">
            <div className="section-heading coverage-heading">
              <p className="eyebrow">{page.sectionLabel}</p>
              <h2>
                {page.sectionTitle[0]}
                <br />
                {page.sectionTitle[1]}
              </h2>
              <p>{page.sectionText}</p>
            </div>

            <div className="coverage-grid">
              {page.coverage.map((item) => {
                const Icon = item.icon;
                return (
                  <article className="coverage-item" key={item.title}>
                    <div className="coverage-icon-badge">
                      <Icon className="coverage-icon" aria-hidden="true" />
                    </div>
                    <h3>{item.title}</h3>
                    <p>{item.text}</p>
                  </article>
                );
              })}
            </div>
          </div>
        </section>

        {pageKey === 'business' ? (
          <section className="enterprise-callout-section">
            <div className="enterprise-callout-container">
              <p className="eyebrow">Enterprise Pricing</p>
              <h2>Need enterprise pricing for a larger environment?</h2>
              <p>
                If your organization has multiple sites, complex security requirements, or a broader
                support scope, contact FixOURTech for enterprise pricing information tailored to your
                Tulsa-area business.
              </p>
              <a className="button button-primary" href={contactPath}>
                Contact Us
              </a>
            </div>
          </section>
        ) : null}
      </main>

      <footer className="contact-bar" id="contact">
        <div className="contact-bar-inner">
          <p className="contact-bar-title">Serving Tulsa, OK and surrounding areas with residential and business IT support.</p>
          <div className="contact-bar-actions">
            <a className="contact-bar-phone" href={phoneHref}>
              (918) 236-7980
            </a>
            <a className="contact-bar-link" href={contactPath}>
              Contact Us
            </a>
          </div>
        </div>
      </footer>
    </div>
  );
}

export default App;
